import React, { useContext, useEffect, useState, useLayoutEffect } from "react";
import useSelectorId from "../../../../utilities/hooks/useSelectorId";
import { ECLLoader } from "../../../index";
import EChartsReact from "echarts-for-react";
import { UserPreferencesContext } from "../../../../utilities/contexts/UserPreferences";
import valueAxis from "./Components/valueAxis";
import categoryAxis from "./Components/categoryAxis";
import debounce from "../../../../utilities/debounce";
import "./ECLGridPlotBlock.css";
import GridPlotLegend from "./Components/legend";
import GridPlotVisualMap from "./Components/visualMap";
import GridPlotGridLabels from "./Components/gridLabels";
import endpointRequest from "../../../../utilities/requests/endpointRequest";
import plotDataReducer from "./Utilities/plotDataReducer";
import gridPlotSeriesTooltip from "./Components/seriesTooltip";
import conditionalColorConfig from "./Components/conditionalColors";
import marklines from "./Components/marklines";
import IconPathMap from "./Components/iconography";
import hexToRgb from "../../../../utilities/hexToRGB";
import { translate } from "../../../../utilities/Translate";
import ECLBlockOptions from "../../components/ECLBlockOptions";

// --- CONSTANTS FOR SIZING AND LAYOUT ---

// Maximum height for non-modal grid
const MAXNONMODALHIEGHT = 600;

// Label, legend, and axis sizing
const GRIDLABELHEIGHT = 20;
const LEGENDHEIGHT = 35;
const HORIZONTALAXISHEIGHT = 20;
const VERTICALAXISWIDTH = 80; // Default, can be adjusted
const POSSIBLEAXES = ["leftAxis", "rightAxis", "bottomAxis", "topAxis"];

// Minimum grid cell dimension
const GRIDMINIMUMDIMENSION = 250;

// Color palette for charts
const ECLIPSEPALETTE = [
   "#FFD100",
   "#3DB9BE",
   "#E04403",
   "#6ADBB8",
   "#008DA3",
   "#FFC692",
   "#AAADCD",
   "#5B1F00",
];

//Alt colors replaces red and green, useful for conditionally colored bars
//ALTCOLORS is assinged a value but never used going to comment out until further inspection
// const ALTCOLORS = [
//    "#FFD100",
//    "#3DB9BE",
//    "#512442",
//    "#6ADBB8",
//    "#008DA3",
//    "#FFC692",
//    "#AAADCD",
//    "#5B1F00",
// ];

/**
 * ECLGridPlotBlock
 * Main component for rendering a grid of ECharts plots, with support for
 * multiple chart types, axes, legends, tooltips, and responsive sizing.
 */

export default function ECLGridPlotBlock(props) {
   const instanceId = useSelectorId(); // Unique ID for this block instance
   const [plotRelations, setPlotRelations] = useState(null); // Plot grid layout info
   const [data, setData] = useState(null); // Raw data for each plot
   const [series, setSeries] = useState(null); // Series config for each plot
   const [bins, setBins] = useState(null); // Bin/category info for axes
   const [error, setError] = useState(null); // Error state
   const [gridSize, setGridSize] = useState(null); // [columns, rows] in grid
   const [plotOffset, setPlotOffset] = useState(0); // Offset for multi-plot cells
   const [gridContainerWidth, setGridContainerWidth] = useState(0); // Width of grid container
   const [modalContainerWidth, setModalContainerWidth] = useState(0); // Width for modal dialog
   const userPrefs = useContext(UserPreferencesContext); // User theme/prefs
   const userTheme = userPrefs.theme;

   // --- DATA FETCHING AND INITIALIZATION ---
   useEffect(() => {
      setData(null);
      setBins(null);
      setSeries(null);
      // Fetch plot layout and data from endpoint
      endpointRequest(
         props.parentObject ? props.parentObject : null,
         props.block ? props.block.id : null,
         props.sessionID,
         props.block ? "plots" : props.endpoint + "plots",
      )
         .then((res) => {
            // Parse grid layout and determine grid size
            let rows = 0;
            let columns = 0;
            let horizontalOffset = 0;
            res.rows.forEach((row) => {
               // Count offset for multi-plot cells
               if (0 === row[0] && 0 === row[1]) {
                  horizontalOffset++;
               }
               if (row[0] > rows) rows = row[0];
               if (row[1] > columns) columns = row[1];
            });
            setPlotOffset(horizontalOffset);
            setPlotRelations(res.rows);
            setGridSize([(rows + 1) * horizontalOffset, columns + 1]);

            if (res.rows.length === 0 || res.rows?.[0][2] === "") {
               setError("No Relations Provided");
               return;
            }

            // Get unique series IDs for all plots
            const seriesIDs = Array.from(new Set(res.rows.map((r) => r[3])));

            // Fetch series config for each plot
            Promise.all(
               seriesIDs.map((r) =>
                  endpointRequest(
                     props.parentObject ? props.parentObject : null,
                     props.block ? props.block.id : null,
                     props.sessionID,
                     props.block
                        ? "config_" + r + "_series"
                        : props.endpoint + "config_" + r + "_series",
                  ),
               ),
            ).then((results) => {
               setSeries(
                  results
                     .map((r, i) =>
                        r.rows.map((b) => {
                           let seriesConfig = {
                              label: b[0]
                                 .replaceAll("(", "")
                                 .replaceAll(")", "")
                                 .trim(),
                              id: b?.[1],
                           };
                           const thisPlotConfig = props.plotConfigs.find(
                              (p) => p.id === seriesIDs[i],
                           );
                           seriesConfig = {
                              ...seriesConfig,
                              ...thisPlotConfig.series.find(
                                 (p) =>
                                    p.id.toLowerCase() === b[1].toLowerCase(),
                              ),
                           };
                           return { config: seriesConfig, id: seriesIDs[i] };
                        }),
                     )
                     .flat(),
               );
            });

            // Fetch bin/category info for each plot
            Promise.all(
               seriesIDs.map((r) =>
                  endpointRequest(
                     props.parentObject ? props.parentObject : null,
                     props.block ? props.block.id : null,
                     props.sessionID,
                     props.block
                        ? "config_" + r + "_bins"
                        : props.endpoint + "config_" + r + "_bins",
                  ),
               ),
            ).then((results) => {
               setBins(
                  results.map((r, i) => {
                     if (r.columns.length > 1) {
                        return {
                           bins: {
                              primary: r.rows.map((b) => b[0]),
                              secondary: r.rows.map((b) => b[1]),
                           },
                           id: seriesIDs[i],
                        };
                     }
                     return { bins: r.rows.flat(), id: seriesIDs[i] };
                  }),
               );
            });

            // Fetch raw data for each plot cell
            Promise.all(
               res.rows.map((r) =>
                  endpointRequest(
                     props.parentObject ? props.parentObject : null,
                     props.block ? props.block.id : null,
                     props.sessionID,
                     props.block ? r[2] : props.endpoint + r[2],
                  ),
               ),
            ).then((results) => {
               setData(
                  results.map((r) =>
                     r.rows.map((b) => [
                        b[0].replaceAll("(", "").replaceAll(")", "").trim(),
                        ...b.slice(1),
                     ]),
                  ),
               );
            });
         })
         .catch((error) => {
            setError("Error fetching plot data: " + error.message);
         });
   }, [props.block?.data, props.block?.react.refresh]);

   // --- RESPONSIVE SIZING AND RESIZE HANDLING ---
   useLayoutEffect(() => {
      // Set initial container widths
      const gridContainer = document.querySelector(
         `#plot-grid-container-${instanceId}`,
      );
      if (gridContainer) {
         setGridContainerWidth(
            gridContainer.getBoundingClientRect().width - 20,
         );
      }
      // Modal sizing for large/small screens
      if (window.innerWidth > 768 && window.innerHeight > 768) {
         setModalContainerWidth(window.innerWidth * 0.8 - 40 - 5 - 2);
      } else {
         setModalContainerWidth(window.innerWidth - 60);
      }
      // Debounced resize handler for window and app container
      const resizeFunc = debounce(() => {
         const gridContainer = document.querySelector(
            `#plot-grid-container-${instanceId}`,
         );
         const modalContainer = document.querySelector(
            `#plot-grid-modal-container-${instanceId}`,
         );
         if (gridContainer) {
            setGridContainerWidth(
               gridContainer.getBoundingClientRect().width - 20,
            );
         }
         if (modalContainer) {
            setModalContainerWidth(
               gridContainer.getBoundingClientRect().width - 20,
            );
         }
      }, 1000);
      // Listen for custom width toggle and window resize
      const appWidthContainer = document.querySelector("#app-width-container");
      if (appWidthContainer) {
         appWidthContainer.addEventListener("widthToggled", resizeFunc);
      }
      window.addEventListener("resize", resizeFunc);
      return () => {
         window.removeEventListener("resize", resizeFunc);
         const appWidthContainer = document.querySelector(
            "#app-width-container",
         );
         if (appWidthContainer) {
            appWidthContainer.removeEventListener("widthToggled", resizeFunc);
         }
      };
   }, [modalContainerWidth]);

   // --- MODAL HEIGHT CALCULATION ---
   let modalContentHeight;
   //Modals go full screen whenever we have less than 769 x 769 client size.
   if (window.innerWidth > 768 && window.innerHeight > 768) {
      //modals take up to 80dvh on larger screens
      modalContentHeight = window.innerHeight * 0.8 - 45;
   } else {
      //modals are full screen, so either fill screen, or if a smaller screen, use 510
      modalContentHeight =
         window.innerHeight > 400 ? window.innerHeight - 45 : 510;
   }
   const modalVizHeight = modalContentHeight - 30; //offset padding/margins

   // --- LOADING AND ERROR STATES ---
   if (!data || !gridSize || !bins || !series || error) {
      return (
         <div className="plot-block-container">
            {props.block && <label>{props.block?.data?.name}</label>}
            <div
               id={`plot-grid-container-${instanceId}`}
               style={{ width: "100%", height: "400px" }}
               className="plot-grid-container"
            >
               <ECLLoader message={error} />
            </div>
         </div>
      );
   }

   // Misconfigured plot block fallback
   if (props.plotConfigs.length < 1 && !data) {
      <div className="plot-block-container">
         <label>{props.block?.data?.name}</label>
         <div
            id={`plot-grid-container-${instanceId}`}
            style={{ width: "100%", height: "400px" }}
            className="plot-grid-container"
         >
            <ECLLoader message={translate("Misconfigured Plot Block")} />
         </div>
      </div>;
   }

   // --- HEATMAP SERIES DETECTION ---
   let heatSeriesIndices = props.plotConfigs
      .map((p) => {
         if (p.series.some((s) => s.type === "heatmap")) {
            return [];
         }
         return 1;
      })
      .filter((p) => p !== 1);

   // --- AXIS RESERVATION FLAGS ---
   const reservedAxes = {
      leftAxis: props.plotConfigs.some((p) => p.leftAxis),
      rightAxis: props.plotConfigs.some((p) => p.rightAxis),
      topAxis: props.plotConfigs.some((p) => p.topAxis),
      bottomAxis: props.plotConfigs.some((p) => p.bottomAxis),
   };

   /**
    * canvasSizer
    * Calculates the minimum and desired canvas size for the grid,
    * including offsets for axes, legends, and labels.
    */
   function canvasSizer(canvasDisplay) {
      let minWidth = 20; //Padding
      let minHeight = 20;
      let topOffset = 10;
      let leftOffset = 20;

      //If we're not hiding legend, and it's not a heat map, legend
      if (!props.block?.data?.noLegend && heatSeriesIndices.length === 0) {
         minHeight = minHeight + LEGENDHEIGHT; //Legend offset
         topOffset = topOffset + LEGENDHEIGHT;
      }

      // X-axis sizing and grid label offsets
      const colLabelsExist = plotRelations.every((p) => p[4] !== "");
      if (reservedAxes.topAxis) {
         minHeight = minHeight + HORIZONTALAXISHEIGHT;
         topOffset = topOffset + HORIZONTALAXISHEIGHT;
         if (!props.block?.data?.noGridMarkLabels && colLabelsExist) {
            minHeight = minHeight + GRIDLABELHEIGHT;
            topOffset = topOffset + GRIDLABELHEIGHT;
         }
      }

      if (reservedAxes.bottomAxis) {
         minHeight = minHeight + HORIZONTALAXISHEIGHT;
         if (!props.block?.data?.noGridMarkLabels && colLabelsExist) {
            minHeight = minHeight + GRIDLABELHEIGHT;
         }
      }

      // Y-axis sizing and grid label offsets
      const rowLabelsExist = plotRelations.every((p) => p[5] !== "");
      let greatestHorizontalLabelLength = 0;
      props.plotConfigs.forEach((p) => {
         if (p.leftAxis?.labelDisplayLength) {
            if (greatestHorizontalLabelLength < p.leftAxis.labelDisplayLength) {
               greatestHorizontalLabelLength = p.leftAxis.labelDisplayLength;
            }
         }
         if (p.rightAxis?.labelDisplayLength) {
            if (
               greatestHorizontalLabelLength < p.rightAxis.labelDisplayLength
            ) {
               greatestHorizontalLabelLength = p.rightAxis.labelDisplayLength;
            }
         }
      });

      let yAxisWidth =
         greatestHorizontalLabelLength !== 0
            ? greatestHorizontalLabelLength * 7 + 20 //give 7px for each char, plus 20 for padding (11 was the additive in the label/value axis, but this spaces just about right)
            : VERTICALAXISWIDTH;

      if (reservedAxes.leftAxis) {
         minWidth = minWidth + yAxisWidth;
         leftOffset = leftOffset + yAxisWidth;
         if (!props.block?.data?.noGridMarkLabels && rowLabelsExist) {
            minWidth = minWidth + GRIDLABELHEIGHT;
            leftOffset = leftOffset + GRIDLABELHEIGHT;
         }
      } else {
         if (rowLabelsExist) {
            leftOffset = leftOffset + 15;
         }
      }

      if (reservedAxes.rightAxis) {
         minWidth = minWidth + yAxisWidth;
         if (!props.block?.data?.noGridMarkLabels && rowLabelsExist) {
            minWidth = minWidth + GRIDLABELHEIGHT;
         }
      }

      // Add grid cell dimensions
      minWidth = minWidth + gridSize[0] * GRIDMINIMUMDIMENSION;
      minHeight = minHeight + gridSize[1] * GRIDMINIMUMDIMENSION;

      // Calculate available space for grid cells
      const desiredHeight =
         canvasDisplay === "modal"
            ? modalVizHeight
            : Math.min(minHeight, MAXNONMODALHIEGHT);
      //Grid sizing, used to space out the plots
      const availableGridWidth =
         canvasDisplay === "modal"
            ? Math.max(modalContainerWidth - minWidth, 0)
            : Math.max(gridContainerWidth - minWidth, 0);
      const availableGridHeight = Math.max(desiredHeight - minHeight, 0);
      const gridWidth = GRIDMINIMUMDIMENSION + availableGridWidth / gridSize[0];
      const gridHeight =
         GRIDMINIMUMDIMENSION + availableGridHeight / gridSize[1];
      return {
         gridWidth: gridWidth,
         gridHeight: gridHeight,
         leftOffset: leftOffset,
         topOffset: topOffset,
         desiredHeight: desiredHeight,
         minHeight: minHeight,
         minWidth: minWidth,
      };
   }

   // Calculate sizes for normal and modal display
   const normalSizes = canvasSizer();
   const modalSizes = canvasSizer("modal");

   /**
    * canvasConfigurer
    * Builds the ECharts option object for the grid of plots,
    * including axes, series, grid layout, legend, tooltips, and more.
    */
   function canvasConfigurer(sizer) {
      //Track your plots for grid/axis assignment
      let gridIndex = 0;
      let axisIndexY = 0;
      let axisIndexX = 0;

      //And get your items ready for assignment
      let xAxis = [];
      let yAxis = [];
      let seriesValues = [];
      let grid = [];

      //Build grid layout and assign axes/series for each cell
      Array.from({ length: gridSize[1] }).map((_, rowIndex) => {
         Array.from({ length: gridSize[0] }).map((_, colIndex) => {
            //Since there can be multiple plots in a single grid cell, invoke dark magic
            const colIndexAdjusted = Math.floor(colIndex / plotOffset);
            const relatedPlotRelations = plotRelations.filter(
               (c) => c[0] === colIndexAdjusted && c[1] === rowIndex,
            );
            const plotRelation = relatedPlotRelations[colIndex % plotOffset];
            const plotConfig = props.plotConfigs.find(
               (p) => p.id === plotRelation[3],
            );

            //Check whether we have all value axes (scatters, lines), all category axes (heatmap) or no axes (pie)
            const allValueAxes = POSSIBLEAXES.every(
               (a) =>
                  plotConfig?.[a]?.type === "log" ||
                  plotConfig?.[a]?.type === "value" ||
                  !plotConfig?.[a]?.type,
            );
            const allCategoryAxes = POSSIBLEAXES.every(
               (a) =>
                  plotConfig?.[a]?.type === "category" ||
                  !plotConfig?.[a]?.type,
            );
            const noAxes = POSSIBLEAXES.every((a) => !plotConfig?.[a]?.type);
            //Parse categories
            let categories;
            let secondaryCategories;
            if (!allValueAxes) {
               if (allCategoryAxes) {
                  categories = bins.find((b) => b.id === plotRelation[3])?.bins
                     ?.primary;
                  while (categories.at(-1) === "") {
                     categories.pop();
                  }
                  secondaryCategories = bins.find(
                     (b) => b.id === plotRelation[3],
                  )?.bins?.secondary;
                  while (secondaryCategories.at(-1) === "") {
                     secondaryCategories.pop();
                  }
               } else {
                  categories = bins.find((b) => b.id === plotRelation[3])?.bins;
               }
            }

            //Using data and plot config, break up data for echarts digestion
            const seriesData = plotDataReducer(
               data[gridIndex],
               plotConfig,
               allValueAxes,
               allCategoryAxes,
               noAxes,
               categories,
               secondaryCategories,
               series,
            );

            //Create Grids
            grid.push({
               width: sizer.gridWidth - 20,
               height: sizer.gridHeight - 20,
               top: sizer.topOffset + rowIndex * sizer.gridHeight,
               left: sizer.leftOffset + colIndex * sizer.gridWidth,
               backgroundColor: userTheme === "Dark" ? "#191919" : "white",
               show: true,
            });

            let yAxesAssigned = 0;
            let xAxesAssigned = 0;

            //Assign Axes
            POSSIBLEAXES.forEach((a) => {
               if (plotConfig[a]) {
                  let axisConf = {
                     position: a.split("Axis")[0],
                  };
                  let axisOrientation;
                  let hideLabels;
                  switch (a) {
                     case "leftAxis":
                        hideLabels = colIndex > 0;
                        axisOrientation = "y";
                        break;
                     case "rightAxis":
                        hideLabels = colIndex !== gridSize[0] - 1;
                        axisOrientation = "y";
                        break;
                     case "bottomAxis":
                        hideLabels = rowIndex !== gridSize[1] - 1;
                        axisOrientation = "x";
                        break;
                     case "topAxis":
                        hideLabels = rowIndex > 0;
                        axisOrientation = "x";
                        break;
                  }

                  if (
                     plotConfig[a].type === "value" ||
                     plotConfig[a].type === "log"
                  ) {
                     axisConf = {
                        ...axisConf,
                        ...valueAxis(
                           userTheme,
                           axisOrientation,
                           plotConfig[a],
                           hideLabels,
                           plotConfig.onZero,
                        ),
                        gridIndex: gridIndex,
                     };

                     if (plotConfig.series.every((s) => s.type === "line")) {
                        axisConf = {
                           ...axisConf,
                           boundaryGap: false,
                        };
                     }
                     // Special handling for log axis min
                     if (plotConfig[a].type === "log") {
                        axisConf = {
                           ...axisConf,
                           minorSplitLine: {
                              show: true,
                              lineStyle: {
                                 color: ["#C4C4C4", "#B6B8B9"],
                              },
                           },
                           min: axisConf.min
                              ? axisConf.min
                              : (value) => {
                                   //Gotta iterate over
                                   let logCounter = 0;
                                   let logHolder = value.min - 0.1;
                                   if (logHolder < 1) {
                                      return 1e-10;
                                   }
                                   if (logHolder < 10) {
                                      return 1;
                                   }
                                   while (logHolder >= 10) {
                                      logHolder = logHolder / 10;
                                      logCounter++;
                                   }
                                   return Math.pow(10, logCounter);
                                },
                        };
                     }
                  }

                  if (plotConfig[a].type === "category") {
                     axisConf = {
                        ...axisConf,
                        ...categoryAxis(
                           userTheme,
                           axisOrientation,
                           plotConfig[a],
                           hideLabels,
                           plotConfig.onZero,
                        ),
                        data: categories,
                        gridIndex: gridIndex,
                     };

                     if (
                        plotConfig.series.every((s) => s.type === "heatmap") &&
                        (a === "leftAxis" || a === "rightAxis")
                     ) {
                        axisConf = {
                           ...axisConf,
                           data: secondaryCategories,
                        };
                     }

                     if (plotConfig.series.every((s) => s.type === "line")) {
                        axisConf = {
                           ...axisConf,
                           boundaryGap: false,
                        };
                     }
                  }

                  if (a === "rightAxis" || a === "leftAxis") {
                     yAxis.push(axisConf);
                     yAxesAssigned++;
                  } else {
                     xAxis.push(axisConf);
                     xAxesAssigned++;
                  }
               }
            });

            //need to find the correct axis for secondary axis
            const thisXAxis = xAxis[axisIndexX];
            const thisYAxis = yAxis[axisIndexY];

            //get series
            //SERIES STUFF!!s
            seriesData.forEach((s, i) => {
               let passedSeriesConfig = s.seriesConfig
                  ? s.seriesConfig
                  : series.find((t) => t.config.label === s.series);
               if (passedSeriesConfig) {
                  let seriesConfig = {
                     type: passedSeriesConfig.config.type,
                     name: s.series,
                     data: s.data,
                     xAxisIndex:
                        passedSeriesConfig.config.useSecondaryAxis &&
                        thisXAxis.type === "value"
                           ? axisIndexX + 1
                           : axisIndexX,
                     yAxisIndex:
                        passedSeriesConfig.config.useSecondaryAxis &&
                        thisYAxis.type === "value"
                           ? axisIndexY + 1
                           : axisIndexY,
                     symbol: `path://${IconPathMap("circle")}`,
                     symbolSize: (x) => {
                        if (
                           !(typeof x === "object" && x[2]) ||
                           passedSeriesConfig.config.type === "boxplot" ||
                           passedSeriesConfig.config.type === "pie"
                        ) {
                           return 8;
                        }
                        if (passedSeriesConfig.config.type === "line") return 2;
                        return Math.max(Math.min(x[2], 100), 8); // If you don't set scale, going to fall back on this, some value between 4-100
                     },
                  };
                  //SYMBOLS
                  if (passedSeriesConfig.config.symbol) {
                     seriesConfig = {
                        ...seriesConfig,
                        symbol: passedSeriesConfig.config.symbol.type
                           ? `path://${IconPathMap(passedSeriesConfig.config.symbol.type)}`
                           : `path://${IconPathMap("circle")}`,
                     };

                     if (passedSeriesConfig.config.symbol.scale) {
                        seriesConfig = {
                           ...seriesConfig,
                           symbolSize: (x) => {
                              return Math.max(
                                 x[2] * passedSeriesConfig.config.symbol.scale,
                                 6,
                              );
                           },
                        };
                     }

                     if (passedSeriesConfig.config.symbol.size) {
                        seriesConfig = {
                           ...seriesConfig,
                           symbolSize: passedSeriesConfig.config.symbol.size,
                        };
                     }
                  }

                  //MARKLINES
                  if (passedSeriesConfig.config.markLines) {
                     seriesConfig = {
                        ...seriesConfig,
                        ...marklines(userTheme, passedSeriesConfig, categories),
                     };
                  }

                  //CONDITIONAL COLOR
                  if (passedSeriesConfig.config.conditionalColor) {
                     let conditionalColors = ["#00C08B", "#E04403"];

                     if (passedSeriesConfig.config.conditionalColor.above) {
                        conditionalColors[0] =
                           passedSeriesConfig.config.conditionalColor.above;
                     }

                     if (passedSeriesConfig.config.conditionalColor.below) {
                        conditionalColors[1] =
                           passedSeriesConfig.config.conditionalColor.below;
                     }

                     seriesConfig = {
                        ...seriesConfig,
                        ...conditionalColorConfig(
                           passedSeriesConfig,
                           conditionalColors,
                        ),
                     };
                  }

                  //BAR SPECIFIC
                  if (plotConfig.noBarGap) {
                     let barWidth = Math.ceil(
                        (sizer.gridWidth - 20) /
                           categories.length /
                           plotConfig.series.filter((p) => p.type === "bar")
                              .length,
                     );
                     if (
                        plotConfig.topAxis?.type === "value" ||
                        plotConfig.bottomAxis?.type === "value" ||
                        plotConfig.topAxis?.type === "log" ||
                        plotConfig.bottomAxis?.type === "log"
                     ) {
                        barWidth = Math.ceil(
                           (sizer.gridHeight - 20) /
                              categories.length /
                              plotConfig.series.filter((p) => p.type === "bar")
                                 .length,
                        );
                     }
                     seriesConfig = {
                        ...seriesConfig,
                        barGap: 0,
                        barCategoryGap: 0,
                        barWidth: barWidth,
                     };
                  }

                  if (passedSeriesConfig.config.stackIndex) {
                     seriesConfig = {
                        ...seriesConfig,
                        stack: passedSeriesConfig.config.stackIndex + gridIndex,
                     };
                  }

                  if (plotConfig.overlaid) {
                     seriesConfig = {
                        ...seriesConfig,
                        barGap: "-100%",
                     };
                  }

                  //LINE SPECIFIC
                  if (passedSeriesConfig.config.area) {
                     seriesConfig = {
                        ...seriesConfig,
                        areaStyle: {
                           origin: "start",
                        },
                     };
                     if (plotConfig.onZero) {
                        seriesConfig.areaStyle = {
                           origin: "auto",
                        };
                     }
                  }

                  if (passedSeriesConfig.config.stepped) {
                     seriesConfig = {
                        ...seriesConfig,
                        step: passedSeriesConfig.config.stepped,
                     };
                  }

                  if (passedSeriesConfig.config.lineStyle) {
                     seriesConfig = {
                        ...seriesConfig,
                        lineStyle: {
                           ...seriesConfig.lineStyle,
                           type: passedSeriesConfig.config.lineStyle,
                        },
                     };
                  }

                  if (passedSeriesConfig.config.smooth) {
                     seriesConfig = {
                        ...seriesConfig,
                        smooth: 1,
                     };
                  }

                  //Pie Specific
                  if (passedSeriesConfig.config.type === "pie") {
                     seriesConfig = {
                        ...seriesConfig,
                        radius: "80%",
                        width: sizer.gridWidth - 40, //Give 20px padding on each side of pie
                        height: sizer.gridHeight - 40,
                        top: sizer.topOffset + rowIndex * sizer.gridHeight + 20,
                        left:
                           sizer.leftOffset + colIndex * sizer.gridWidth + 20,
                        label: {
                           color: userTheme === "Dark" ? "white" : "black",
                           show: true,
                        },
                     };
                  }

                  // BOXPLOT SPECIFIC
                  if (passedSeriesConfig.config.type === "boxplot") {
                     const colorObj = hexToRgb(
                        props.block?.data?.palette
                           ? props.block.data.palette[
                                i % props.block?.data?.palette.length
                             ]
                           : ECLIPSEPALETTE[i % ECLIPSEPALETTE.length],
                     );
                     seriesConfig = {
                        ...seriesConfig,
                        itemStyle: {
                           ...seriesConfig.itemStyle,
                           borderColor: props.block?.data?.palette
                              ? props.block.data.palette[
                                   i % props.block.data.palette.length
                                ]
                              : ECLIPSEPALETTE[i % ECLIPSEPALETTE.length],
                           borderWidth: 2,
                           color: `rgba(${colorObj.r}, ${colorObj.g}, ${colorObj.b}, .5)`,
                        },
                     };
                  }
                  //MEASURES!
                  if (passedSeriesConfig.config.type === "measure") {
                     seriesConfig = {
                        ...seriesConfig,
                        type: "custom",
                        coordinateSystem: "none",
                        renderItem: (params, api) => {
                           return {
                              type: "group",
                              children: [
                                 {
                                    type: "text",
                                    cursor: "normal",
                                    style: {
                                       font: '16px "Noto Sans", sans-serif',
                                       overflow: "truncate",
                                       ellipsis: "...",
                                       fill:
                                          userTheme === "Dark"
                                             ? "white"
                                             : "black",
                                       textAlign: "center",
                                       text: params.seriesName,
                                       y:
                                          sizer.topOffset -
                                          20 +
                                          sizer.gridHeight / 2 +
                                          sizer.gridHeight * rowIndex,
                                       x:
                                          sizer.leftOffset +
                                          sizer.gridWidth / 2 +
                                          sizer.gridWidth * colIndex -
                                          10,
                                       width: sizer.gridWidth,
                                    },
                                 },
                                 {
                                    type: "line",
                                    cursor: "normal",
                                    shape: {
                                       x1:
                                          sizer.leftOffset +
                                          sizer.gridWidth * colIndex +
                                          20 -
                                          10,
                                       y1:
                                          sizer.topOffset -
                                          3 +
                                          sizer.gridHeight / 2 +
                                          sizer.gridHeight * rowIndex,
                                       x2:
                                          sizer.leftOffset +
                                          sizer.gridWidth * colIndex +
                                          sizer.gridWidth -
                                          20 -
                                          10,
                                       y2:
                                          sizer.topOffset -
                                          3 +
                                          sizer.gridHeight / 2 +
                                          sizer.gridHeight * rowIndex,
                                    },
                                    style: {
                                       stroke: api.style().fill,
                                       fill: api.style().fill,
                                       lineWidth: 2,
                                    },
                                    enterFrom: {
                                       style: {
                                          opacity: 0,
                                       },
                                    },
                                 },
                                 {
                                    type: "text",
                                    cursor: "normal",
                                    style: {
                                       font: 'bolder 18px "Noto Sans", sans-serif',
                                       overflow: "truncate",
                                       ellipsis: "...",
                                       fill:
                                          userTheme === "Dark"
                                             ? "white"
                                             : "black",
                                       textAlign: "center",
                                       text: api.value(0),
                                       y:
                                          sizer.topOffset +
                                          sizer.gridHeight / 2 +
                                          sizer.gridHeight * rowIndex,
                                       x:
                                          sizer.leftOffset +
                                          sizer.gridWidth / 2 +
                                          sizer.gridWidth * colIndex -
                                          10,
                                       width: sizer.gridWidth,
                                    },
                                 },
                              ],
                           };
                        },
                     };
                  }

                  //TOOLTIPS!
                  seriesConfig = {
                     ...seriesConfig,
                     tooltip:
                        seriesConfig.type === "custom"
                           ? {}
                           : gridPlotSeriesTooltip(
                                userTheme,
                                seriesConfig,
                                seriesValues,
                                xAxis,
                                yAxis,
                                categories,
                                secondaryCategories,
                                props.block?.data?.palette
                                   ? props.block.data.palette
                                   : ECLIPSEPALETTE,
                             ),
                  };

                  seriesValues.push(seriesConfig);

                  //HEATMAP STUFF
                  if (passedSeriesConfig.config.type === "heatmap") {
                     heatSeriesIndices[plotRelation[3]].push(
                        seriesValues.length - 1,
                     );
                  }
               }
            });
            //Update axis indices, incremenet grid index
            axisIndexX = axisIndexX + xAxesAssigned;
            axisIndexY = axisIndexY + yAxesAssigned;
            gridIndex++;
         });
      });
      //Grid Labels
      let graphicsConfig = GridPlotGridLabels(
         userTheme,
         plotRelations,
         props.plotConfigs,
         sizer,
         plotOffset,
      );

      let chartConfig = {
         series: seriesValues,
         tooltip: { trigger: "grid" },
         color: props.block?.data?.palette
            ? props.block.data.palette
            : ECLIPSEPALETTE,
         grid: grid,
         xAxis: xAxis,
         yAxis: yAxis,
         legend:
            !props.block?.data?.noLegend &&
            heatSeriesIndices.length === 0 &&
            GridPlotLegend(
               userTheme,
               props.plotConfigs,
               seriesValues,
               LEGENDHEIGHT,
               ECLIPSEPALETTE,
            ), //Not hiding legend/not heatmap
         graphic: {
            elements: graphicsConfig,
         },
         textStyle: {
            color: userTheme === "Dark" ? "white" : "black",
         },
         backgroundColor: userTheme === "Dark" ? "#222222" : "#EFEFEE",
      };

      //Add Visualmap and labels for heat maps
      if (
         props.plotConfigs.some((p) =>
            p.series.some((s) => s.type === "heatmap"),
         )
      ) {
         chartConfig = {
            ...chartConfig,
            series: chartConfig.series.map((s) => {
               return {
                  ...s,
                  label: {
                     show: true,
                     formatter: (d) => {
                        //Format to show labels on heatmap only if space allows
                        const labelSizeEstimate =
                           d.data[2].toString().length * 7;
                        const availableWidth = sizer.gridWidth - 100; //width minus left offset
                        const availableHeight = sizer.gridHeight - 10; //height minus top offset
                        if (
                           labelSizeEstimate >
                              availableWidth /
                                 xAxis[s.xAxisIndex].data.length ||
                           20 >
                              availableHeight / yAxis[s.yAxisIndex].data.length
                        ) {
                           return "";
                        }
                        //Otherwise return data fixed to 2 decimal places
                        return Number.parseFloat(d.data[2]).toFixed(2);
                     },
                  },
               };
            }),
            visualMap: GridPlotVisualMap(
               heatSeriesIndices,
               seriesValues,
               props.plotConfigs,
            ),
         };
      }
      return chartConfig;
   }

   return (
      <div
         className="plot-block-container"
         style={{
            height:
               Math.min(normalSizes.desiredHeight, normalSizes.minHeight) +
               60 +
               "px",
         }}
      >
         <ECLBlockOptions
            block={props.block}
            instanceId={props.instanceId}
            expandWidget={
               <EChartsReact
                  style={{
                     height: modalVizHeight,
                     width: Math.max(modalContainerWidth, modalSizes.minWidth),
                     borderRadius: "10px",
                  }}
                  option={canvasConfigurer(modalSizes)}
               />
            }
         />
         <div
            className="plot-grid-content-box"
            style={{
               height:
                  Math.min(normalSizes.desiredHeight, normalSizes.minHeight) +
                  10 +
                  5 +
                  "px",
            }}
         >
            <div
               id={`plot-grid-container-${instanceId}`}
               style={{
                  width: "100%",
                  minWidth: normalSizes.minWidth,
                  maxWidth: "100%",
               }} //Really want the width value to stick
               className="plot-grid-container"
            >
               <EChartsReact
                  style={{
                     height: normalSizes.minHeight,
                     borderRadius: "10px",
                  }}
                  option={canvasConfigurer(normalSizes)}
               />
            </div>
         </div>
      </div>
   );
}
